import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight,
  Brain,
  Zap,
  Upload
} from 'lucide-react';

export const Home: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleReducedMotionChange);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      mediaQuery.removeEventListener('change', handleReducedMotionChange);
    };
  }, []);

  // Preload the brain image
  useEffect(() => {
    const img = new Image();
    img.src = '/brain-sides-concept-illustration.png';
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true);
  }, []);

  // Generate random stars for background
  const stars = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 1,
    opacity: Math.random() * 0.8 + 0.2,
    animationDelay: Math.random() * 3
  }));

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black relative overflow-hidden">
      {/* Animated Background Stars */}
      <div className="absolute inset-0">
        {stars.map((star) => (
          <motion.div
            key={star.id}
            className="absolute bg-white rounded-full"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
            }}
            animate={{
              opacity: [star.opacity, star.opacity * 0.3, star.opacity],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: star.animationDelay,
            }}
          />
        ))}
      </div>

      {/* Gradient Orbs - Reduced and repositioned */}
      <div className="absolute top-1/4 left-10 w-32 h-32 md:w-64 md:h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-1/4 right-10 w-32 h-32 md:w-64 md:h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

      {/* Interactive cursor effect - hide on mobile */}
      {!prefersReducedMotion && (
        <motion.div
          className="absolute w-6 h-6 bg-purple-400 rounded-full pointer-events-none mix-blend-screen filter blur-sm opacity-30 hidden lg:block"
          animate={{
            x: mousePosition.x - 12,
            y: mousePosition.y - 12,
          }}
          transition={{
            type: "spring",
            damping: 30,
            stiffness: 200,
          }}
        />
      )}

      {/* Main Content */}
      <div className="relative z-10 h-full flex items-center justify-center lg:items-center">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12 items-center">
            
            {/* Left Content - Hero Section */}
            <div className="lg:col-span-7 text-center lg:text-left order-2 lg:order-1 flex flex-col items-center lg:items-start">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-5 sm:space-y-6 lg:space-y-8 w-full"
              >
                {/* Pre-headline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="inline-flex items-center space-x-2 bg-purple-500/10 backdrop-blur-sm border border-purple-500/20 rounded-full px-3 py-1 sm:px-4 sm:py-2"
                >
                  <Zap className="h-4 w-4 text-purple-400" />
                  <span className="text-xs sm:text-sm font-medium text-purple-300">AI-Powered Learning Platform</span>
                </motion.div>

                {/* Main Headline - Improved Typography */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight tracking-tight"
                >
                  <span className="text-white">Transform</span>
                  <br />
                  <span className="text-white">Your</span>
                  <br />
                  <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                    Learning
                  </span>
                </motion.h1>

                {/* Value Proposition */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                >
                  Upload any document and let <span className="text-purple-400 font-semibold">EcstaticAI</span> create 
                  personalized study materials, flashcards, and quizzes in seconds.
                </motion.p>

                {/* CTA Section - Enhanced */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start w-full"
                >
                  <Link
                    to="/dashboard"
                    className="group relative bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-blue-600 transition-all duration-300 flex items-center justify-center space-x-2 sm:space-x-3 shadow-2xl hover:shadow-purple-500/25 text-sm sm:text-base transform hover:scale-105"
                  >
                    <Brain className="h-5 w-5" />
                    <span>Start Learning Now</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 blur opacity-75 group-hover:opacity-100 transition-opacity -z-10"></div>
                  </Link>
                  
                  <Link
                    to="/upload"
                    className="border-2 border-purple-400/50 text-purple-300 px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-semibold hover:bg-purple-400/10 hover:border-purple-400 transition-all duration-300 flex items-center justify-center space-x-2 sm:space-x-3 backdrop-blur-sm text-sm sm:text-base"
                  >
                    <Upload className="h-5 w-5" />
                    <span>Upload Document</span>
                  </Link>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Side - Visual Element */}
            <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center items-center w-full">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.3 }}
                className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 mx-auto"
                style={{ willChange: 'transform' }}
              >
                {/* Brain Image Container with optimized floating animation */}
                <motion.img
                  src="/brain-sides-concept-illustration.png"
                  alt="AI Brain Concept"
                  className="w-full h-full object-contain filter drop-shadow-2xl gpu-accelerated"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{
                    opacity: imageLoaded ? 1 : 0,
                    scale: imageLoaded ? 1 : 0.9,
                  }}
                  transition={{
                    opacity: { duration: 0.5 },
                    scale: { duration: 0.5 },
                    delay: 0.5,
                  }}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                  style={{ 
                    willChange: 'transform',
                    animation: imageLoaded && !prefersReducedMotion ? 'brain-float 4s ease-in-out infinite' : 'none'
                  }}
                />

                {/* Enhanced floating elements - improved responsive design */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 1 }}
                  className="absolute -left-4 top-1/3 bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-md border border-purple-400/30 rounded-2xl px-4 py-3 shadow-xl hidden sm:block gpu-accelerated"
                >
                  <div className="text-purple-300 text-xs sm:text-sm font-semibold">Neural Flow</div>
                  <div className="text-purple-400/70 text-[10px] sm:text-xs">Active Learning</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="absolute -right-4 bottom-1/3 bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-md border border-blue-400/30 rounded-2xl px-4 py-3 shadow-xl hidden sm:block gpu-accelerated"
                >
                  <div className="text-blue-300 text-xs sm:text-sm font-semibold">Smart Sync</div>
                  <div className="text-blue-400/70 text-[10px] sm:text-xs">Adaptive AI</div>
                </motion.div>

                {/* Floating particles - optimized for performance */}
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full hidden sm:block gpu-accelerated"
                    style={{
                      left: `${20 + i * 30}%`,
                      top: `${20 + i * 20}%`,
                      willChange: 'transform',
                      animation: !prefersReducedMotion ? `particle-float 3s ease-in-out infinite ${i * 0.8}s` : 'none'
                    }}
                  />
                ))}

                {/* Loading placeholder */}
                {!imageLoaded && !imageError && (
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full animate-pulse flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-brain-loading"></div>
                  </div>
                )}

                {imageError && (
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
                    <div className="text-center">
                      <Brain className={`w-16 h-16 text-purple-400 mx-auto mb-2 ${!prefersReducedMotion ? 'animate-brain-float' : ''}`} />
                      <span className="text-purple-300 text-sm">AI Brain Concept</span>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};